/**
 * IP-based Tracking System
 * Single source of truth: ipMap/{ipSig} document
 * No Meta campaign logic, no fingerprinting, just IP signatures
 */

import { db, ensureAnonAuth } from "./firebase";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  increment,
  arrayUnion,
} from "firebase/firestore";
import { getIpSignature } from "./utils/ipSig";
import { setActiveIpSig, resolveActiveIpSig } from "./utils/ipActive";
import { isMetaSession, trackMetaSource, getMetaSourceData, setMetaLocally } from "./utils/metaDetection";

let lastClickTs = 0; // Simple debounce
let allowIpWritesCache = null; // null = unknown, true/false = gated (per tab session)

/**
 * Gate: Darf in ipMap/{ipSig} geschrieben werden?
 * - true, wenn isMetaSession() == true (Neu-Anlage erlaubt)
 * - sonst nur, wenn ipMap/{ipSig} bereits EXISTIERT und isFromMeta==true (Rehydration)
 *   (in dem Fall rehydrieren wir lokal via setMetaLocally())
 * OPTIMIZATION: Caches result per tab session to avoid redundant getDoc calls
 * @param {string} ipSig - IP signature hash
 * @returns {Promise<boolean>} true if write is allowed
 */
async function canWriteToIpDoc(ipSig) {
  // If already Meta session (from URL or localStorage), allow write
  if (isMetaSession()) {
    allowIpWritesCache = true;
    return true;
  }

  // Check cache first (avoid redundant Firestore reads)
  if (allowIpWritesCache !== null) return allowIpWritesCache;

  // Check if IP doc exists with Meta flag
  const ref = doc(db, "ipMap", ipSig);
  const snap = await getDoc(ref);

  if (snap.exists() && snap.data()?.isFromMeta === true) {
    // Rehydrate local session (helps event functions)
    setMetaLocally();
    console.log(`[Gate] ✓ IP-Rehydration: Allowing write to existing Meta IP doc`);
    allowIpWritesCache = true;
    return allowIpWritesCache;
  }

  // No Meta session and no existing Meta IP doc → deny write
  allowIpWritesCache = false;
  return allowIpWritesCache;
}

/**
 * Initializes IP-based tracking on first page visit.
 * CRITICAL: Only creates ipMap document for Meta campaign traffic (?meta=true).
 * REHYDRATION: If IP already exists in ipMap (from previous Meta visit), rehydrate and track.
 * Creates or updates the ipMap document for the current IP signature.
 * Uses Transaction to prevent race conditions between multiple tabs.
 */
export async function initIpTracking() {
  try {
    // (0) CRITICAL: Capture ?meta=true IMMEDIATELY before any await
    //     This is idempotent and prevents race conditions with Router/Pixel URL cleaning
    trackMetaSource();

    await ensureAnonAuth();

    // 1) IP-Signatur immer zuerst ermitteln
    const ipSig = await getIpSignature().catch(() => null);
    if (!ipSig) {
      console.warn("⚠️ No IP signature available - skipping tracking init");
      return;
    }
    setActiveIpSig(ipSig);

    const ipRef = doc(db, "ipMap", ipSig);
    const ua = navigator.userAgent || "";
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";

    // 3) Check if document already exists (needed for both paths)
    const existingSnap = await getDoc(ipRef);

    // 4) Gating:
    //    - Wenn Meta: Erstanlage erlaubt (setDoc merge → erstellt/updated)
    //    - Wenn NICHT Meta: nur updaten, falls Doc bereits existiert als Meta-Doc
    if (isMetaSession()) {
      // ERSTANLAGE ERLAUBT (createdAt nur setzen, wenn noch nicht vorhanden)
      const payload = {
        isFromMeta: true,
        metaSource: getMetaSourceData(),
        lastSeenAt: serverTimestamp(),
        userAgents: arrayUnion(ua),
        timezones: arrayUnion(tz),
      };

      // createdAt NUR beim allerersten Anlegen setzen
      if (!existingSnap.exists() || !existingSnap.data()?.createdAt) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(ipRef, payload, { merge: true });
      console.log(`✓ Meta IP initialized/updated: ${ipSig.slice(0, 16)}...`);
    } else {
      // KEINE ERSTANLAGE – nur Rehydration falls vorhanden
      if (existingSnap.exists() && existingSnap.data()?.isFromMeta === true) {
        // rehydrate local flag
        setMetaLocally();
        // optional: lastSeenAt + UA/TZ pflegen (Doc existiert ja sicher)
        await setDoc(
          ipRef,
          {
            lastSeenAt: serverTimestamp(),
            userAgents: arrayUnion(ua),
            timezones: arrayUnion(tz),
          },
          { merge: true }
        );
        console.log(`[IP-Rehydration] ✓ Using existing Meta IP: ${ipSig.slice(0, 16)}...`);
      } else {
        console.log("⊘ Not from Meta and no prior Meta IP doc — skip init");
      }
    }
  } catch (e) {
    console.error("❌ Error initializing IP tracking:", e);
  }
}

/**
 * Tracks a contact button click.
 * Only tracks if user is from Meta campaign OR IP doc exists.
 * Uses setDoc with merge:true and increment(1) for race-proof atomic updates.
 * CRITICAL: Uses canWriteToIpDoc() gate to prevent creating docs for non-Meta users.
 */
export async function trackContactClick(source = "hero") {
  const ipSig = await resolveActiveIpSig(getIpSignature);
  if (!ipSig) return;

  // debounce
  const now = Date.now();
  if (now - lastClickTs < 300) return;
  lastClickTs = now;

  try {
    await ensureAnonAuth();

    // Gate prüfen: erlaubt der Zustand die Erstellung/den Write?
    const allowed = await canWriteToIpDoc(ipSig);
    if (!allowed) {
      console.log("⊘ Click skipped (no meta & no prior Meta IP doc)");
      return;
    }

    const ipRef = doc(db, "ipMap", ipSig);

    await setDoc(
      ipRef,
      {
        lastSeenAt: serverTimestamp(),
        lastClickSource: source,
        clickCount: increment(1),
      },
      { merge: true }
    );
    console.log(`✓ Contact click tracked: ${source}`);
  } catch (error) {
    console.error("❌ Error tracking contact click:", error);
    console.error("❌ Error code:", error.code);
  }
}

/**
 * Tracks a page view.
 * Only tracks if user is from Meta campaign OR IP doc exists.
 * Uses setDoc with merge:true and increment(1) for race-proof atomic updates.
 * CRITICAL: Uses canWriteToIpDoc() gate to prevent creating docs for non-Meta users.
 */
export async function trackPageView(path = window.location.pathname) {
  const ipSig = await resolveActiveIpSig(getIpSignature);
  if (!ipSig) return;

  try {
    await ensureAnonAuth();

    // Gate prüfen: erlaubt der Zustand die Erstellung/den Write?
    const allowed = await canWriteToIpDoc(ipSig);
    if (!allowed) {
      // Keine Meta-Session und kein existierendes Meta-IP-Doc → nicht schreiben
      console.log("⊘ PageView skipped (no meta & no prior Meta IP doc)");
      return;
    }

    const ipRef = doc(db, "ipMap", ipSig);
    const ua = navigator.userAgent || "";
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";

    await setDoc(
      ipRef,
      {
        lastSeenAt: serverTimestamp(),
        lastPageView: path,
        pageViewCount: increment(1),
        userAgents: arrayUnion(ua),
        timezones: arrayUnion(tz),
      },
      { merge: true }
    );
    console.log(`✓ Page view tracked: ${path}`);
  } catch (error) {
    console.error("❌ Error tracking page view:", error);
  }
}
